"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ArrowCtaButton } from "@/components/ui/arrow-cta-button";
import type { CtaIntent, CtaLocation } from "@/lib/cta-tracking";
import { cn } from "@/lib/utils";

export interface EmailCaptureFormProps {
  emailPlaceholder: string;
  /** Submit button label in its idle state. */
  submitLabel: string;
  sendingLabel?: string;
  onSubmitEmail: (email: string) => Promise<void> | void;
  isSubmitting?: boolean;
  /**
   * Allow submitting with the field left empty. The empty string is passed to
   * onSubmitEmail so the caller decides what an email-less submit means.
   */
  allowEmpty?: boolean;
  className?: string;
  inputClassName?: string;
  formClassName?: string;
  /** hero-form uses the shared email capture pill button layout (Figma 4074:23618). */
  buttonLayout?: "hero-form" | "default";
  /** PostHog CTA tracking on the submit button. See docs/analytics/cta-tracking.md. */
  ctaIntent?: CtaIntent;
  ctaLocation?: CtaLocation;
  hideArrowOnMobile?: boolean;
  disabled?: boolean;
  id?: string;
  prefillEmail?: string;
}

interface EmailCaptureValues {
  email: string;
}

/**
 * Single-field email capture shared by apply-now and newsletter forms.
 * Callers supply submit behaviour while this component owns validation.
 */
export function EmailCaptureForm({
  emailPlaceholder,
  submitLabel,
  sendingLabel = "Sending…",
  onSubmitEmail,
  isSubmitting = false,
  allowEmpty = false,
  className,
  inputClassName,
  formClassName,
  buttonLayout = "default",
  ctaIntent,
  ctaLocation,
  hideArrowOnMobile = false,
  disabled = false,
  id = "email-capture",
  prefillEmail = "",
}: EmailCaptureFormProps) {
  const t = useTranslations("Form");
  const schema = React.useMemo(() => {
    const email = z
      .string()
      .trim()
      .email({ message: t("invalidEmail") });

    return z.object({
      email: allowEmpty
        ? email.or(z.literal(""))
        : email.min(1, { message: t("required") }),
    });
  }, [allowEmpty, t]);
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<EmailCaptureValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: prefillEmail },
  });
  const errorId = `${id}-error`;

  React.useEffect(() => {
    setValue("email", prefillEmail);
  }, [prefillEmail, setValue]);

  const submit = handleSubmit(async ({ email }) => {
    if (isSubmitting || disabled) return;
    await onSubmitEmail(email);
  });

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className={cn("relative", formClassName, className)}
      noValidate
    >
      <label htmlFor={id} className="sr-only">
        {emailPlaceholder}
      </label>
      <input
        id={id}
        type="email"
        required={!allowEmpty}
        autoComplete="email"
        aria-describedby={errors.email ? errorId : undefined}
        aria-invalid={Boolean(errors.email)}
        placeholder={emailPlaceholder}
        disabled={isSubmitting || disabled}
        className={inputClassName}
        {...register("email")}
      />
      <ArrowCtaButton
        type="submit"
        layout={buttonLayout === "hero-form" ? "hero-form" : undefined}
        hideArrowOnMobile={hideArrowOnMobile}
        labelSize={buttonLayout === "hero-form" ? undefined : "md"}
        disabled={isSubmitting || disabled}
        data-ph-capture-attribute-cta_label={submitLabel}
        data-ph-capture-attribute-cta_intent={ctaIntent}
        data-ph-capture-attribute-cta_location={
          ctaIntent ? ctaLocation : undefined
        }
      >
        {isSubmitting ? sendingLabel : submitLabel}
      </ArrowCtaButton>
      {errors.email ? (
        <p
          id={errorId}
          role="alert"
          className="text-body-xs text-text-critical absolute top-full left-4 z-10 mt-1"
        >
          {errors.email.message}
        </p>
      ) : null}
    </form>
  );
}
